<template>
  <div class="iod-results">
    <h2 class="filter-header" v-if="impactAreaOptions.length > 1">Select Impact Areas</h2>
    <ChipsInput
      v-if="impactAreaOptions.length > 1"
      :items="impactAreaOptions"
      v-model="selectedImpactAreas"
      :selected-options="impactAreas"
      :show-icons="true"
      @input="(newVal) => handleImpactAreaClick(newVal)"
    />
    <CopyTrackWrapper type="impact_of_disability">
      <Transition
        :name="computedTransition"
        mode="out-in">
        <EditableWikiTextArea
          :key="statement"
          aria-live="polite"
          :class="{'empty-statement': isStatementEmptyPlaceholder}"
          v-model="statement"
          :show-buttons="!isStatementEmptyPlaceholder"
          v-if="statement.length > 0"
          @textEdited="handleTextEdit"
        />
      </Transition>
    </CopyTrackWrapper>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import ChipsInput from '@/components/ChipsInput'
import { getIODStatementsByImpactArea } from '@/api/impact-of-disability.js'
import { buildIODStatement } from '@/components/impact-of-disability/statement-builder.js'
import EditableWikiTextArea from '@/components/EditableWikiTextArea'
import CopyTrackWrapper from '@/components/CopyTrackWrapper'
import RedundantRouteMixin from '@/components/RedundantRouteMixin.js'

export default {
  name: 'ImpactOfDisabilityResults',
  components: {
    ChipsInput,
    EditableWikiTextArea,
    CopyTrackWrapper
  },
  mixins: [RedundantRouteMixin],
  props: {
    disability: {
      type: String,
      default: ''
    },
    subjects: {
      type: Array,
      default: () => []
    },
    impactAreas: {
      type: Array,
      default: () => []
    },
    impactAreaOptions: {
      type: Array,
      default: () => []
    },
    changeRequiresConfirmation: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedImpactAreas: this.impactAreas,
      statementOptions: {},
      statement: '',
      unEditedStatement: '',
      isStatementEmptyPlaceholder: false,
    }
  },
  computed: {
    ...mapGetters('contentProfile', ['profileId']),
    ...mapGetters('visibility', ['betaVisibility']),
    computedTransition() {
      return this.changeRequiresConfirmation ? 'none' : 'fade-in'
    }
  },
  async created() {
    if (this.disability) {
      await this.pieceStatementsTogether()
    }
    this.$sendEvent('Viewed Impact of Disability Statement Results')
  },
  methods: {
    async getStatements() {
      const statements = await getIODStatementsByImpactArea(this.disability, this.profileId)
      if (!this.statementOptions[this.disability]) {
        this.statementOptions[this.disability] = statements.statements;
      }
    },
    async pieceStatementsTogether() {
      if (!this.statementOptions[this.disability]) {
        await this.getStatements()
      }
      [this.statement, this.isStatementEmptyPlaceholder] = buildIODStatement({
        impactAreaStatements: this.impactAreaOptions,
        subjectStatements: this.statementOptions,
        selectedDisability: this.disability,
        selectedSubjects: this.subjects,
        selectedImpactAreas: this.impactAreas
      })
      this.$emit('update:changeRequiresConfirmation', false)
      this.unEditedStatement = this.statement
    },
    updateRoute() {
      let newQuery = Object.assign({}, this.query)
      newQuery.subjects = this.subjects
      newQuery.disability = this.disability
      newQuery.impactAreas = this.impactAreas.map(ia => ia.area_uuid)
      this.rerouteIfRouteChanged(this.$route, {
        query: newQuery,
        name: 'ImpactOfDisability'
      })
    },
    async processWatchedParam(newVal, oldVal) {
      if (oldVal !== newVal) {
        await this.pieceStatementsTogether()
        this.updateRoute()
      }
    },
    handleImpactAreaClick(newVal) {
      if (this.changeRequiresConfirmation) {
        this.$emit('confirmupdate:impactAreas', newVal)
      } else {
        this.$emit('update:impactAreas', this.selectedImpactAreas)
      }
    },
    handleTextEdit(text) {
      if (text.trim().replace(/(\s)/gm, '') !== this.unEditedStatement.trim().replace(/(\s)/gm, '')) {
        this.$emit('update:changeRequiresConfirmation', true)
        this.$sendEvent('Edited Impact of Disability Statement', {'edited_text': text})
      }
    }
  },
  watch: {
    disability: async function(newVal, oldVal) {
      await this.processWatchedParam(newVal, oldVal)
    },
    impactAreas: async function(newVal, oldVal) {
      await this.processWatchedParam(newVal, oldVal)
    },
    subjects: async function(newVal, oldVal) {
      await this.processWatchedParam(newVal, oldVal)
    }
  }
}
</script>
<style lang="less" scoped>
@import '~@/variables.less';
.iod-results {
  border: 1px solid @gray-light;
  border-radius: 4px;
  padding: 20px 40px 40px 40px;
}
.filter-header {
  font-size: 1rem;
  font-weight: 700;
  line-height: 20px;
  color: @gray;
  margin: 0 0 5px 0;
  padding: 5px 0;
  text-transform: uppercase;
}
/deep/ .checkboxes-container {
  border-bottom: 1px solid @gray-light;
}
/deep/ .checkbox-item {
  padding-left: 0;
  padding-bottom: 0.5rem;
  label {
    font-size: 1rem;
    &.selected {
      .chip-icon {
        font-size: 1.5rem;
      }
    }
  }
  .chip-icon {
    font-size: 1.2rem;
  }
}
/deep/ .rendered-text-container {
  font-size: 21px;
  line-height: 33px;
  padding: 2rem 1rem 0 1rem;
  p {
    margin-bottom: 1.5rem;
  }
}
/deep/ .empty-statement .rendered-text-container {
  font-style: italic;
}

/deep/ textarea {
  margin-top: 15px;
  font-weight: 400;
}
/deep/ .wiki-text-container {
  max-width: 720px;
}
.fade-in-enter-active {
  transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
  transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}
</style>